import React from 'react';
import PropTypes from 'prop-types';
import {
    Refresh as RefreshIcon,
    Add as AddIcon,
    ViewColumn as KanbanIcon,
    List as ListIcon,
} from '@material-ui/icons';
import BreadcrumbLayout from '../../../shared/components/common/breadcrumbLayout';
import {
    Table,
    KanbanBoard,
    PageToolbar,
    SelectField,
    SegmentedControl,
    PrimaryButton,
    SecondaryButton,
    QuotationCard,
    FeedbackState,
} from '../../../shared/ui';
import {
    MainContent,
    ViewModeContainer,
    ViewModeLabel
} from '../styles';

const ServiceStatusView = ({
    loading,
    error,
    viewMode,
    onChangeViewMode,
    selectedProduct,
    onChangeProduct,
    productFilterItems,
    kanbanGroups,
    columnStates,
    onToggleExpand,
    onToggleShowAll,
    listColumns,
    listRows,
    onRefresh,
    onNewQuote,
    onOpenDetails,
    resolveStatusVariant,
    currentLanguage,
}) => {
    const clientLang = currentLanguage.bepiService.client;
    const sharedLang = currentLanguage.bepiService.shared;

    const breadcrumbItems = [
        { label: clientLang.client_quotations_home_breadcrumb, path: '/bepi/client' },
        { label: clientLang.client_quotations_page_title },
    ];

    return (
        <BreadcrumbLayout
            breadcrumbItems={breadcrumbItems}
            title={clientLang.client_quotations_page_title}
            showBackButton
            currentLanguage={currentLanguage}
        >
            <MainContent $compact>
                <PageToolbar
                    leftContent={(
                        <SelectField
                            id='client-quotations-product-filter'
                            label={clientLang.client_quotations_filter_product_label}
                            value={selectedProduct}
                            onChange={onChangeProduct}
                            options={productFilterItems}
                        />
                    )}
                    rightContent={(
                        <>
                            <SecondaryButton
                                onClick={onRefresh}
                                disabled={loading}
                                title={clientLang.client_quotations_refresh_btn}
                            >
                                <RefreshIcon />
                                <span className='hide-mobile'>
                                    {clientLang.client_quotations_refresh_btn}
                                </span>
                            </SecondaryButton>

                            <PrimaryButton onClick={onNewQuote}>
                                <AddIcon />
                                {clientLang.client_quotations_new_quote_btn}
                            </PrimaryButton>

                            <ViewModeContainer>
                                <ViewModeLabel>{clientLang.client_quotations_view_mode_label}</ViewModeLabel>
                                <SegmentedControl
                                    value={viewMode}
                                    onChange={onChangeViewMode}
                                    ariaLabel={clientLang.client_quotations_view_mode_aria_label}
                                    options={[
                                        { value: 'kanban', title: clientLang.client_quotations_view_kanban, icon: <KanbanIcon /> },
                                        { value: 'list', title: clientLang.client_quotations_view_list, icon: <ListIcon /> },
                                    ]}
                                />
                            </ViewModeContainer>
                        </>
                    )}
                />

                {loading ? (
                    <FeedbackState
                        loading
                        loadingMessage={sharedLang.shared_equip_form_loading_msg}
                    />
                ) : error ? (
                    <FeedbackState error={error} />
                ) : (
                    <>
                        {viewMode === 'kanban' && (
                            <KanbanBoard
                                groups={kanbanGroups}
                                columnStates={columnStates}
                                onToggleExpand={onToggleExpand}
                                onToggleShowAll={onToggleShowAll}
                                emptyMessage={sharedLang.shared_quotation_column_no_quotes}
                                renderCard={(quote) => (
                                    <QuotationCard
                                        key={quote.id}
                                        quote={quote}
                                        onClick={() => onOpenDetails(quote)}
                                        statusVariant={resolveStatusVariant(quote.status)}
                                        waitingLabel={sharedLang.shared_quotation_card_waiting}
                                    />
                                )}
                            />
                        )}

                        {viewMode === 'list' && (
                            <Table
                                columns={listColumns}
                                rows={listRows}
                                rowKey='id'
                                defaultRowsPerPage={10}
                                emptyMessage={clientLang.client_quotations_no_quotations}
                                defaultSortField='data'
                                defaultSortOrder='desc'
                            />
                        )}
                    </>
                )}
            </MainContent>
        </BreadcrumbLayout>
    );
};

ServiceStatusView.propTypes = {
    loading: PropTypes.bool,
    error: PropTypes.string,
    viewMode: PropTypes.oneOf(['kanban', 'list']).isRequired,
    onChangeViewMode: PropTypes.func.isRequired,
    selectedProduct: PropTypes.string.isRequired,
    onChangeProduct: PropTypes.func.isRequired,
    productFilterItems: PropTypes.array.isRequired,
    kanbanGroups: PropTypes.object.isRequired,
    columnStates: PropTypes.object.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    onToggleShowAll: PropTypes.func.isRequired,
    listColumns: PropTypes.array.isRequired,
    listRows: PropTypes.array.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onNewQuote: PropTypes.func.isRequired,
    onOpenDetails: PropTypes.func.isRequired,
    resolveStatusVariant: PropTypes.func.isRequired,
    currentLanguage: PropTypes.object.isRequired,
};

ServiceStatusView.defaultProps = {
    loading: false,
    error: null,
};

export default ServiceStatusView;
